/** FOIA requests under PIO: the seven steps with a person, a date and files on each; fees; export to Excel. */
import { useState } from 'react';
import { FOIA_STEPS } from '@civilos/domain';
import { useAuth } from '../shared/auth.tsx';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState } from './common.tsx';
import { FileList, UploadButton } from './WorkFiles.tsx';

const money = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }));

export function FoiaPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState('open');
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/foia?status=${status}&q=${encodeURIComponent(q)}`), [status, q]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="PIO"
        title="FOIA requests"
        sub="Each request moves through the office’s seven steps. Whoever’s step it is sees it on their Home."
        actions={
          r.data?.canManage && (
            <div className="row gap-sm">
              <a className="btn" href="/api/foia/export">
                Export to Excel
              </a>
              <button className="btn" onClick={() => navigate(at('/pio/foia/settings'))}>
                Steps & fees
              </button>
              <button className="btn primary" onClick={() => navigate(at('/pio/foia/new'))}>
                New request
              </button>
            </div>
          )
        }
      />
      <div className="row gap-sm wrap">
        <div className="seg-ctl" role="tablist" aria-label="Which requests">
          {(
            [
              ['open', 'Open'],
              ['closed', 'Closed'],
              ['all', 'All'],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
              {l}
            </button>
          ))}
        </div>
        <input type="search" placeholder="Search number, requester, request…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search FOIA requests" style={{ maxWidth: 280 }} />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.requests.length ? (
        <EmptyState icon="folder" title="No FOIA requests" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Request</th>
                <th>Received</th>
                <th>Step</th>
                <th>With</th>
                <th>Fee</th>
              </tr>
            </thead>
            <tbody>
              {r.data.requests.map((f: any) => (
                <tr key={f.id} className="clickable" onClick={() => navigate(at(`/pio/foia/${f.id}`))}>
                  <td>
                    <strong>{f.requester}</strong>
                    <div className="small muted">
                      <span className="mono">{f.number}</span> · {f.request.slice(0, 90)}
                      {f.request.length > 90 ? '…' : ''}
                    </div>
                  </td>
                  <td>{day(f.received_on)}</td>
                  <td>{f.current ? <span className="small">{`${f.current.n}. ${f.current.label}`}</span> : <Badge tone="ok">Closed</Badge>}</td>
                  <td>{f.current?.personName ?? (f.current ? <span className="muted">Not assigned</span> : '—')}</td>
                  <td>{money(f.fee.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function NewFoia() {
  const { navigate } = useRouter();
  const [f, setF] = useState<Record<string, string>>({ receivedOn: new Date().toLocaleDateString('en-CA') });
  const a = useAction();
  return (
    <div className="ops stack">
      <PageHeader crumb="FOIA requests" title="New FOIA request" sub="Step 1 starts with the person set under Steps & fees." />
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => navigate(at(`/pio/foia/${(await api.post<{ id: string }>('/api/foia', f)).id}`), true));
        }}
      >
        <Card title="The request">
          <div className="grid-3">
            <label>
              Requester
              <input required value={f.requester ?? ''} onChange={(e) => setF({ ...f, requester: e.target.value })} placeholder="Name or organization" />
            </label>
            <label>
              Contact (email / phone / address)
              <input value={f.contact ?? ''} onChange={(e) => setF({ ...f, contact: e.target.value })} />
            </label>
            <label>
              Received on
              <input type="date" required value={f.receivedOn ?? ''} onChange={(e) => setF({ ...f, receivedOn: e.target.value })} />
            </label>
          </div>
          <label>
            What they asked for
            <textarea rows={5} required value={f.request ?? ''} onChange={(e) => setF({ ...f, request: e.target.value })} />
          </label>
        </Card>
        <ErrorBox error={a.error} />
        <div>
          <button className="btn primary" disabled={a.busy}>
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function StepCard({ id, s, people, current, canManage, me, onDone }: { id: string; s: any; people: { id: string; name: string }[]; current: number | null; canManage: boolean; me: string; onDone: () => void }) {
  const a = useAction();
  const [doneOn, setDoneOn] = useState<string>(s.doneOn ?? '');
  const [note, setNote] = useState<string>(s.note ?? '');
  const mine = s.personId === me;
  const isCurrent = current === s.n;
  const canEdit = canManage || mine;
  const save = (body: Record<string, unknown>) => a.run(async () => (await api.post(`/api/foia/${id}/step`, { n: s.n, ...body }), onDone()));
  return (
    <li className={`foia-step ${s.doneOn ? 'done' : isCurrent ? 'cur' : ''}`}>
      <span className="foia-n">{s.doneOn ? '✓' : s.n}</span>
      <div className="stack xs" style={{ flex: 1, minWidth: 0 }}>
        <div className="row between wrap gap-sm">
          <strong>{s.label}</strong>
          {isCurrent && <Badge tone="warn">{mine ? 'Your step' : 'Up now'}</Badge>}
        </div>
        <div className="grid-3">
          <label>
            Person
            <select value={s.personId ?? ''} disabled={!canManage || a.busy} onChange={(e) => save({ personId: e.target.value || null })}>
              <option value="">— {s.defaultWho} —</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Date done
            <input type="date" value={doneOn} disabled={!canEdit || a.busy} onChange={(e) => setDoneOn(e.target.value)} />
          </label>
          <label>
            Note
            <input value={note} disabled={!canEdit || a.busy} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
        {canEdit && (
          <div className="row gap-sm wrap">
            {(doneOn !== (s.doneOn ?? '') || note !== (s.note ?? '')) && (
              <button type="button" className="btn primary sm" disabled={a.busy} onClick={() => save({ doneOn: doneOn || null, note })}>
                Save
              </button>
            )}
            {!s.doneOn && isCurrent && !doneOn && (
              <button type="button" className="btn primary sm" disabled={a.busy} onClick={() => save({ doneOn: new Date().toLocaleDateString('en-CA'), note })}>
                Done today
              </button>
            )}
            <UploadButton url={`/api/foia/${id}/files`} item={`step-${s.n}`} onDone={onDone} label="Upload for this step" />
          </div>
        )}
        <ErrorBox error={a.error} />
      </div>
    </li>
  );
}

export function FoiaDetail({ id }: { id: string }) {
  const { me } = useAuth();
  const r = useLoad(() => api.get<any>(`/api/foia/${id}`), [id]);
  const a = useAction();
  const [fee, setFee] = useState<Record<string, string> | null>(null);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { request: f, files, people, canManage } = r.data;
  const fv = fee ?? { hours: f.hours ?? '', pages: f.pages ?? '', hourlyRate: f.hourly_rate ?? '', pageRate: f.page_rate ?? '', otherFees: f.other_fees ?? '' };
  const numOrNull = (s: string) => (s === '' || s == null ? null : Number(s));
  return (
    <div className="ops stack">
      <PageHeader crumb="FOIA requests" title={`${f.number} · ${f.requester}`} sub={`Received ${day(f.received_on)}${f.contact ? ` · ${f.contact}` : ''}`} actions={f.status === 'closed' ? <Badge tone="ok">Closed {f.closed_at ? day(f.closed_at) : ''}</Badge> : <Badge tone="warn">Step {f.current?.n} of 7</Badge>} />
      <ErrorBox error={a.error} />
      <Card title="What they asked for">
        <p className="pre">{f.request}</p>
      </Card>
      <Card title="Steps">
        <ol className="foia-steps">
          {f.steps.map((s: any) => (
            <StepCard key={`${s.n}-${s.doneOn}-${s.personId}-${s.note}`} id={id} s={s} people={people} current={f.current?.n ?? null} canManage={canManage} me={me?.user.id ?? ''} onDone={r.reload} />
          ))}
        </ol>
      </Card>
      <div className="grid-2-cards">
        <Card title="Fees">
          <div className="grid-2">
            {(
              [
                ['hours', 'Hours of staff time', '0.25'],
                ['hourlyRate', 'Hourly rate ($)', '0.01'],
                ['pages', 'Pages', '1'],
                ['pageRate', 'Per page ($)', '0.01'],
                ['otherFees', 'Other (media, postage) ($)', '0.01'],
              ] as const
            ).map(([k, l, step]) => (
              <label key={k}>
                {l}
                <input type="number" min="0" step={step} value={fv[k] ?? ''} onChange={(e) => setFee({ ...fv, [k]: e.target.value })} placeholder={k.endsWith('Rate') ? 'Not set yet' : ''} />
              </label>
            ))}
          </div>
          <dl className="kv" style={{ marginTop: 10 }}>
            <dt>Staff time</dt>
            <dd>{f.fee.labor == null ? '—' : `${f.hours} h × ${money(f.hourly_rate)} = ${money(f.fee.labor)}`}</dd>
            <dt>Copies</dt>
            <dd>{f.fee.copies == null ? '—' : `${f.pages} × ${money(f.page_rate)} = ${money(f.fee.copies)}`}</dd>
            <dt>Total</dt>
            <dd>
              <strong>{money(f.fee.total)}</strong>
            </dd>
          </dl>
          {f.fee.missingRates && <Notice tone="warn">The hourly rate or per-page rate isn’t set yet, so the total leaves that part out.</Notice>}
          {fee && (
            <button
              className="btn primary sm"
              disabled={a.busy}
              onClick={() => a.run(async () => (await api.post(`/api/foia/${id}`, { hours: numOrNull(fv.hours!), pages: numOrNull(fv.pages!), hourlyRate: numOrNull(fv.hourlyRate!), pageRate: numOrNull(fv.pageRate!), otherFees: numOrNull(fv.otherFees!) }), setFee(null), r.reload()))}
            >
              Save fees
            </button>
          )}
        </Card>
        <Card title="Files" actions={<UploadButton url={`/api/foia/${id}/files`} onDone={r.reload} />}>
          <FileList files={files} itemLabel={(it) => (it.startsWith('step-') ? `Step ${it.slice(5)}` : it)} />
        </Card>
      </div>
    </div>
  );
}

export function FoiaSettings() {
  const r = useLoad(() => api.get<any>('/api/foia/settings'));
  const a = useAction();
  const [d, setD] = useState<Record<string, string> | null>(null);
  const [rates, setRates] = useState<{ hourlyRate: string; pageRate: string } | null>(null);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const defs = d ?? Object.fromEntries(FOIA_STEPS.map((s) => [String(s.n), r.data.defaults[String(s.n)] ?? '']));
  const rv = rates ?? { hourlyRate: r.data.hourlyRate ?? '', pageRate: r.data.pageRate ?? '' };
  return (
    <div className="ops stack">
      <PageHeader crumb="FOIA requests" title="Steps & fees" sub="Who each step goes to on a new request, and the fee rates. Leave a rate blank until it’s decided." />
      <ErrorBox error={a.error} />
      <Card title="Who does each step">
        <div className="stack sm">
          {FOIA_STEPS.map((s) => (
            <label key={s.n} className="row gap-sm wrap" style={{ alignItems: 'center' }}>
              <span style={{ minWidth: 320 }}>
                {s.n}. {s.label} <span className="muted small">({s.who})</span>
              </span>
              <select value={defs[String(s.n)] ?? ''} onChange={(e) => setD({ ...defs, [String(s.n)]: e.target.value })} aria-label={`Step ${s.n} person`} style={{ maxWidth: 280 }}>
                <option value="">— nobody yet —</option>
                {r.data.people.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.title ? ` — ${p.title}` : ''}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      </Card>
      <Card title="Fees">
        <div className="grid-3">
          <label>
            Hourly rate ($)
            <input type="number" min="0" step="0.01" value={rv.hourlyRate} onChange={(e) => setRates({ ...rv, hourlyRate: e.target.value })} placeholder="Not set yet" />
          </label>
          <label>
            Per page ($)
            <input type="number" min="0" step="0.01" value={rv.pageRate} onChange={(e) => setRates({ ...rv, pageRate: e.target.value })} placeholder="Not set yet" />
          </label>
        </div>
      </Card>
      <div>
        <button
          className="btn primary"
          disabled={a.busy}
          onClick={() =>
            a.run(async () => {
              await api.post('/api/foia/settings', { defaults: defs, hourlyRate: rv.hourlyRate === '' ? null : Number(rv.hourlyRate), pageRate: rv.pageRate === '' ? null : Number(rv.pageRate) });
              setD(null);
              setRates(null);
              r.reload();
            })
          }
        >
          Save
        </button>
      </div>
    </div>
  );
}
